import React from "react";
import { useNavigate } from "react-router-dom";
import { useSubscriptionPlans } from "../hooks/use-subscription-plans";

interface PlanFeature {
  label: string;
}

interface Plan {
  id: string;
  category: string;
  name: string;
  price: string | number;
  description: string;
  features: PlanFeature[];
  buttonLabel: string;
  isFreeTrial?: boolean;
}

interface SubscriptionPlansProps {
  selectedTab: string;
}

// API에서 데이터를 가져옵니다

/**
 * CheckCircle Icon Component
 */
function CheckCircle() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <path
        d="M13.7504 7.49982C13.9945 7.74389 13.9945 8.13963 13.7504 8.38367L9.60899 12.5251C9.36491 12.7692 8.96924 12.7692 8.72516 12.5251L6.66708 10.467C6.423 10.2229 6.423 9.82725 6.66708 9.58317C6.91116 9.33909 7.30688 9.33909 7.55097 9.58317L9.16708 11.1993L12.8665 7.49982C13.1106 7.25574 13.5063 7.25574 13.7504 7.49982Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.3337 9.99984C18.3337 14.6022 14.6027 18.3332 10.0003 18.3332C5.39795 18.3332 1.66699 14.6022 1.66699 9.99984C1.66699 5.39746 5.39795 1.6665 10.0003 1.6665C14.6027 1.6665 18.3337 5.39746 18.3337 9.99984ZM17.0837 9.99984C17.0837 13.9118 13.9123 17.0832 10.0003 17.0832C6.08831 17.0832 2.91699 13.9118 2.91699 9.99984C2.91699 6.08782 6.08831 2.9165 10.0003 2.9165C13.9123 2.9165 17.0837 6.08782 17.0837 9.99984Z"
        fill="black"
      />
    </svg>
  );
}

/**
 * Plan Card Component
 */
function PlanCard({ plan }: { plan: Plan }) {
  const navigate = useNavigate();
  const isFranchise = plan.category === "프랜차이즈";

  const handleCheckout = () => {
    // 프랜차이즈는 문의하기 페이지로 이동
    if (isFranchise) {
      navigate("/franchise-inquiry");
      return;
    }
    // 나머지는 결제 페이지로 이동
    navigate("/subscription/checkout", {
      state: { plan },
    });
  };

  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 md:p-8 flex flex-col h-full">
      {/* Plan Header */}
      <div className="mb-8">
        <h3 className="text-lg md:text-xl font-light text-gray-600 mb-4">
          {plan.category}
        </h3>

        {/* Price Section */}
        <div className="mb-6">
          <h4 className="text-2xl md:text-3xl font-light text-gray-900 mb-1">
            {plan.name}
          </h4>
          <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {plan.price}
          </div>
          <p className="text-sm md:text-base text-gray-600 leading-relaxed">
            {plan.description}
          </p>
        </div>

        {/* CTA Button */}
        <button
          onClick={handleCheckout}
          className="w-full py-3 px-4 rounded-full border-2 border-orange-500 text-orange-500 font-semibold hover:bg-orange-50 transition-colors text-base md:text-lg"
        >
          {plan.buttonLabel}
        </button>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-300 my-8" />

      {/* Features */}
      <div className="flex-1 flex flex-col gap-4">
        {plan.features.map((feature, index) => (
          <div key={index} className="flex items-start gap-3">
            <CheckCircle />
            <span className="text-sm md:text-base text-gray-800 leading-relaxed">
              {feature.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * SubscriptionPlans Component
 */
export function SubscriptionPlans({ selectedTab }: SubscriptionPlansProps) {
  const { plans, loading } = useSubscriptionPlans(selectedTab);

  if (loading) {
    return (
      <section className="w-full bg-white py-2 pb-10">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 text-center">
          상품 정보를 불러오는 중...
        </div>
      </section>
    );
  }

  return (
    <section className="w-full bg-white py-2 pb-10">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {[...plans]
            .sort((a, b) => a.rawPrice - b.rawPrice)
            .map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
        </div>
      </div>
    </section>
  );
}
